/**
 * WiFi signal helpers.
 *
 * The gateway WiFi level is the decoded `rssiLevel` measure (0-5) of the
 * gateway's own device (the DongleController). It is an envelope metric, not a
 * configured DDF point, so `GET /device` never returns it and it never lands in
 * `pointsById` on its own.
 *
 * However StreamProcessing already emits `rssiLevel`/`rssi` inside the
 * `broadcastTelemetry` SignalR payload (TelemetryMapper does not exclude them),
 * so the controllers reducer keeps it on the device that holds the radio, in a
 * `wifi` field of its own — deliberately not in `pointsById`, which every
 * screen that lists a device's variables enumerates in full. `seedWifiPoints`
 * was the earlier route, seeding points from `device/latest-measures`; it stays
 * off (`ADD_FAKE_POINTS`) and is no longer needed for the level to move.
 */

const RSSI_LEVEL_KEY = "rssilevel";
const RSSI_KEY = "rssi";

/**
 * The WiFi envelope keys, lowercased. Exported because they are not ordinary
 * device points and the rest of the app has to know it: the reducer creates
 * them, and the screens that summarise "has this device any reading at all"
 * must leave them out — a radio level is not a measure of the fridge.
 */
export const WIFI_POINT_KEYS = [RSSI_KEY, RSSI_LEVEL_KEY];

export const isWifiPointKey = (key) =>
  typeof key === "string" && WIFI_POINT_KEYS.includes(key.toLowerCase());

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const findByKey = (pointsById, wantedLowerKey) => {
  if (!pointsById || typeof pointsById !== "object") return undefined;
  const entry = Object.entries(pointsById).find(
    ([key]) => key.toLowerCase() === wantedLowerKey
  );
  return entry?.[1];
};

/** Pull the WiFi level (0-5) out of a latest-measures `measures` object. */
export const extractWifiLevelFromMeasures = (measures) => {
  const point = findByKey(measures, RSSI_LEVEL_KEY);
  return toNumber(point?.value);
};

const extractRssiFromMeasures = (measures) => {
  const point = findByKey(measures, RSSI_KEY);
  return toNumber(point?.value);
};

// This constant is due to the lack of a dedicated point for
// rssi and rssiLevel in the device descriptor files (DDFs).
const ADD_FAKE_POINTS = false;

/**
 * Inject `rssiLevel` and `rssi` points into a device's `pointsById` so the
 * SignalR reducer can keep them live. Existing keys are preserved (not
 * overwritten). Initial values come from the latest-measures object (if given).
 */
export const seedWifiPoints = (pointsById, measures) => {
  const result = { ...pointsById };
  if (!ADD_FAKE_POINTS) return result;

  if (!findByKey(result, RSSI_LEVEL_KEY)) {
    result.rssiLevel = {
      code: "rssiLevel",
      name: "rssiLevel",
      lastValue: extractWifiLevelFromMeasures(measures),
    };
  }
  if (!findByKey(result, RSSI_KEY)) {
    result.rssi = {
      code: "rssi",
      name: "rssi",
      lastValue: extractRssiFromMeasures(measures),
    };
  }

  return result;
};

const readLevelFromPoints = (pointsById) => {
  const point = findByKey(pointsById, RSSI_LEVEL_KEY);
  return toNumber(point?.lastValue);
};

const readRssiFromPoints = (pointsById) => {
  const point = findByKey(pointsById, RSSI_KEY);
  return toNumber(point?.lastValue);
};

const findDongle = (gateway) => {
  const devices = gateway?.devices;
  return Array.isArray(devices)
    ? devices.find((d) => d?.isDongleController)
    : undefined;
};

/**
 * Read the WiFi level (0-5) for a gateway.
 *
 * The dongle inside `gateway.devices` comes from REST, so it carries points but
 * never the live envelope — that one lives on the controller in the store, which
 * `selectControllerWifiLevel` reads. Here the order is: a seeded point on the
 * dongle, then `gateway.wifiLevel` (what the backend last decoded), which is
 * what the indicator shows right after a page load, before the first frame.
 * Returns undefined when neither exists.
 */
export const selectGatewayWifiLevel = (gateway) => {
  const liveLevel = readLevelFromPoints(findDongle(gateway)?.pointsById);
  return liveLevel ?? toNumber(gateway?.wifiLevel);
};

/**
 * Read the raw WiFi signal strength in dBm (the `rssi` measure) for a gateway.
 *
 * Same order as the level above; shown as a detail on the WiFi icon (hover/tap).
 */
export const selectGatewayWifiRssi = (gateway) => {
  const liveRssi = readRssiFromPoints(findDongle(gateway)?.pointsById);
  return liveRssi ?? toNumber(gateway?.wifiRssi);
};

/**
 * Read the WiFi level (0-5) off the device that holds the radio.
 *
 * `controller.wifi` is the live envelope the controllers reducer keeps from the
 * telemetry; the points are the older seeded route (`seedWifiPoints`), still
 * honoured where they exist.
 */
export const selectControllerWifiLevel = (controller) =>
  toNumber(controller?.wifi?.level) ?? readLevelFromPoints(controller?.pointsById);

/** Same, for the raw dBm reading. */
export const selectControllerWifiRssi = (controller) =>
  toNumber(controller?.wifi?.rssi) ?? readRssiFromPoints(controller?.pointsById);
